'use client'

import * as React from 'react'
import { Button } from '@/components/ui/button'
import { DisabledWithReason } from '@/components/ui/disabled-with-reason'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { usePermissions } from '@/hooks/use-permissions'
import { navRequirementMet, routeRequirement } from '@/lib/auth/nav-permissions'
import type { PermissionAction, PermissionSubject } from '@/lib/auth/permissions'
import { useShellTranslation } from '@/lib/shell/use-shell-translation'
import { cn } from '@/lib/utils'

/**
 * Role-aware controls — the owner's rule (2026-10-05): a menu entry the role
 * cannot open is HIDDEN; a write control inside a page stays VISIBLE but
 * DISABLED, saying "Your role can't do this", instead of failing with a 403
 * after the click.
 *
 * Every helper here is fail-open (see hooks/use-permissions.ts): while the
 * grants load, and after a failed fetch, the control renders exactly as it did
 * before. Only a KNOWN "no" hides or disables. The API still refuses the call
 * whatever this renders — this is guidance, not the boundary.
 *
 * Not so the PAGE: the route gate (route-gate.tsx) holds back a page that has a
 * rule until the grants arrive (owner, 2026-10-06), so by the time a control
 * here renders inside such a page the grants are usually known already. The
 * fail-open above still matters on open pages, in the shell, and after a
 * failed fetch.
 *
 * The shapes, smallest first:
 *   - `usePermissionGate(action, subject)` → `{ denied, reason, loading }`, for a
 *     control that already manages its own `disabled` (menu items, switches):
 *     `disabled={busy || gate.denied}` and `title={gate.reason}`.
 *   - `<PermissionButton action subject …buttonProps>` → a `<Button>` that
 *     disables itself, with the reason in a tooltip, a `title` and screen-reader
 *     text.
 *   - `<RequirePermission action subject mode="disable" | "hide">` around any
 *     single element (a Button, a Button asChild Link, a Switch, a Radix
 *     `…Trigger asChild`). Denied, `disabled` and the reason reach the element
 *     actually rendered, through every `asChild` level.
 *   - `<PermissionMenuItem action subject …>` for a row menu entry (denied: the
 *     reason is a visible line inside the item), and
 *     `<WithPermissionGate action subject>{(gate) => …}</WithPermissionGate>`
 *     for JSX that is not a component (a table column's `cell` cannot call a
 *     hook). First written for the phishing row menus.
 */

export const PERMISSION_DENIED_KEY = 'shell.permissions.denied'

export interface PermissionGate {
  /** True only when the grants are KNOWN and do not cover the action. */
  denied: boolean
  /** "Your role can't do this", in the interface language, when denied. */
  reason: string | undefined
  /**
   * The grants are still LOADING: `denied` is false meanwhile. A FAILED fetch is
   * not loading — it is unknown, and everything fails open (a read gated on
   * `!gate.loading && !gate.denied` still runs).
   */
  loading: boolean
}

export function usePermissionGate(action: PermissionAction, subject: PermissionSubject): PermissionGate {
  const { status, can } = usePermissions()
  const { t } = useShellTranslation()
  const denied = can(action, subject) === false
  return {
    denied,
    reason: denied ? t(PERMISSION_DENIED_KEY) : undefined,
    loading: status === 'loading',
  }
}

/**
 * The gate of a LINK: denied when the page at `href` is one the route gate
 * would refuse this role (its own rule in lib/auth/nav-permissions.ts, or its
 * nearest mapped ancestor's). Browser QA, 2026-10-06: read-only roles were
 * offered enabled "New campaign", "Connect", "Set up" and course links whose
 * every click landed on "This page isn't part of your role".
 */
export function useRouteGate(href: string | undefined): PermissionGate {
  const { status, data } = usePermissions()
  const { t } = useShellTranslation()
  const requirement = href ? routeRequirement(href) : undefined
  const denied =
    status === 'ready' && requirement !== undefined && !navRequirementMet(requirement, data?.permissions ?? [])
  return { denied, reason: denied ? t(PERMISSION_DENIED_KEY) : undefined, loading: status === 'loading' }
}

type DisableableProps = {
  disabled?: boolean
  onClick?: (event: React.MouseEvent) => void
  'aria-disabled'?: boolean | 'true' | 'false'
  tabIndex?: number
  title?: string
  asChild?: boolean
  children?: React.ReactNode
}

/**
 * The props that switch an element off. `disabled` covers a real button or a
 * Radix item; the rest covers `<Button asChild><Link/></Button>`, where an
 * anchor ignores `disabled`: the click is swallowed and it leaves the tab order.
 * The reason replaces the element's own `title`: a disabled Button keeps its
 * pointer events (`disabled:pointer-events-auto`), so the browser shows that
 * title on hover — "Delete this campaign" on a control the role cannot use
 * reads as available.
 */
function disabledProps(reason: string | undefined) {
  return {
    disabled: true,
    'aria-disabled': true as const,
    tabIndex: -1,
    title: reason,
    onClick: (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
    },
    'data-permission-denied': 'true',
  }
}

/**
 * Switch an element off all the way down its `asChild` chain. Radix Slot lets
 * the CHILD's own props win over the ones merged into it, so in
 * `<AlertDialogTrigger asChild><Button disabled={isPending}>` the Button's
 * explicit `disabled={false}` (and its `title`) beat the trigger's
 * `disabled: true` — the button rendered enabled. Every element in the chain
 * gets the props itself, so the innermost one, the element actually rendered,
 * says "off" too.
 */
function denyElement(element: React.ReactElement<DisableableProps>, reason: string | undefined): React.ReactElement {
  const { asChild, children } = element.props
  if (asChild && React.isValidElement<DisableableProps>(children)) {
    return React.cloneElement(element, disabledProps(reason), denyElement(children, reason))
  }
  return React.cloneElement(element, disabledProps(reason))
}

export interface RequirePermissionProps {
  action: PermissionAction
  subject: PermissionSubject
  /** `disable` (default): visible, switched off, explained. `hide`: not rendered. */
  mode?: 'disable' | 'hide'
  /** One element that accepts `disabled` (Button, Button asChild, Switch, menu item). */
  children: React.ReactElement<DisableableProps>
  /** What `hide` renders instead (nothing by default). */
  fallback?: React.ReactNode
  /** Tooltip side for `disable`. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Class for the wrapper `disable` adds around the element. */
  className?: string
}

export function RequirePermission({
  action,
  subject,
  mode = 'disable',
  children,
  fallback = null,
  side,
  className,
}: RequirePermissionProps) {
  const gate = usePermissionGate(action, subject)
  if (!gate.denied) return children
  if (mode === 'hide') return <>{fallback}</>
  return (
    <DisabledWithReason disabled reason={gate.reason} side={side} className={className}>
      {denyElement(children, gate.reason)}
    </DisabledWithReason>
  )
}

/**
 * `RequirePermission`, keyed by the page a link opens instead of a grant: one
 * element (a `<Button asChild><Link href/></Button>`, a `<Link>`, a card) that
 * is switched off, with the reason, when the role may not open `href`.
 */
export function RequireRoute({
  href,
  mode = 'disable',
  children,
  fallback = null,
  side,
  className,
}: Omit<RequirePermissionProps, 'action' | 'subject'> & { href: string }) {
  const gate = useRouteGate(href)
  if (!gate.denied) return children
  if (mode === 'hide') return <>{fallback}</>
  return (
    <DisabledWithReason disabled reason={gate.reason} side={side} className={className}>
      {denyElement(children, gate.reason)}
    </DisabledWithReason>
  )
}

/**
 * A settings form the role may READ but not change: every native control
 * inside (inputs, textareas, selects, buttons — so Radix switches, checkboxes
 * and select triggers too) is disabled by the browser itself, because they sit
 * in a disabled `<fieldset>`. Browser QA, 2026-10-06: a dozen settings pages
 * gated only their Save button, so a read-only role could flip every switch and
 * type in every field of a form it could never save.
 *
 * Wrap the FORM, not the page: tabs, "Back" links and previews stay usable
 * outside it. Things a fieldset cannot reach (a rich-text editor, a drag
 * handle) still need their own `readOnly`/`disabled`. The notice says why.
 */
export function PermissionFieldset({
  action,
  subject,
  children,
  className,
  notice,
}: {
  action: PermissionAction
  subject: PermissionSubject
  children: React.ReactNode
  className?: string
  /** Shown above the form while it is read-only. Pass `null` for none. */
  notice?: React.ReactNode
}) {
  const gate = usePermissionGate(action, subject)
  return (
    <fieldset
      disabled={gate.denied}
      data-permission-denied={gate.denied ? 'true' : undefined}
      className={cn('m-0 min-w-0 border-0 p-0', className)}
    >
      {gate.denied && notice !== null ? (
        <p data-permission-reason="" className="mb-3 text-sm text-muted-foreground">
          {notice ?? gate.reason}
        </p>
      ) : null}
      {children}
    </fieldset>
  )
}

export type PermissionButtonProps = React.ComponentProps<typeof Button> & {
  action: PermissionAction
  subject: PermissionSubject
  /** Tooltip side when denied. */
  reasonSide?: 'top' | 'right' | 'bottom' | 'left'
}

/**
 * A `<Button>` that is disabled, with "Your role can't do this", when the role
 * lacks `action` on `subject`. Allowed (or unknown), every prop — including
 * the caller's own `disabled` and `title` — passes through untouched; denied,
 * the reason replaces the title and `disabled` wins, `asChild` included.
 */
export function PermissionButton({ action, subject, reasonSide, ...props }: PermissionButtonProps) {
  const gate = usePermissionGate(action, subject)
  if (!gate.denied) return <Button {...props} />
  return (
    <DisabledWithReason disabled reason={gate.reason} side={reasonSide}>
      {denyElement(<Button {...props} />, gate.reason)}
    </DisabledWithReason>
  )
}

/**
 * A `DropdownMenuItem` that is disabled and inert (no onClick / onSelect) when
 * the role lacks `action` on `subject`, with the reason written INSIDE the item
 * as a small second line. A disabled Radix item takes no pointer events and
 * the arrow keys skip it, so a `title` (or a tooltip) on it is never shown;
 * the visible line is the only channel that reaches everyone (the same shape
 * as the role picker's "cannot give" line, settings/roles/member-role-picker).
 * The line is the item's accessible description; its name stays the action.
 *
 * An `asChild` item (a Link) renders its label only when denied: the entry
 * goes nowhere, and Slot could not take a second child anyway.
 */
export function PermissionMenuItem({
  action,
  subject,
  disabled,
  title,
  onClick,
  onSelect,
  asChild,
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuItem> & { action: PermissionAction; subject: PermissionSubject }) {
  const gate = usePermissionGate(action, subject)
  const reasonId = React.useId()
  if (!gate.denied) {
    return (
      <DropdownMenuItem
        {...props}
        asChild={asChild}
        className={className}
        disabled={disabled}
        title={title}
        onClick={onClick}
        onSelect={onSelect}
      >
        {children}
      </DropdownMenuItem>
    )
  }
  const label =
    asChild && React.isValidElement<{ children?: React.ReactNode }>(children) ? children.props.children : children
  return (
    <DropdownMenuItem
      {...props}
      className={cn('flex-wrap gap-y-0.5', className)}
      disabled
      title={gate.reason}
      aria-describedby={reasonId}
      data-permission-denied="true"
    >
      {label}
      <span
        id={reasonId}
        aria-hidden="true"
        data-permission-reason=""
        className="basis-full text-xs font-normal text-muted-foreground"
      >
        {gate.reason}
      </span>
    </DropdownMenuItem>
  )
}

/** Hands `{ denied, reason, loading }` to JSX that cannot call `usePermissionGate` itself. */
export function WithPermissionGate({
  action,
  subject,
  children,
}: {
  action: PermissionAction
  subject: PermissionSubject
  children: (gate: PermissionGate) => React.ReactNode
}) {
  const gate = usePermissionGate(action, subject)
  return <>{children(gate)}</>
}
